/**
 * @fileoverview Form validation utilities for authentication
 *
 * Provides comprehensive validation functions for authentication forms including
 * email validation, password strength checking, EDS form validation, and security
 * features like rate limiting and debounced validation.
 *
 * @author Claude Code
 * @since Phase 3 - State Management Refactor
 */

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Password validation rules
const PASSWORD_RULES = {
  minLength: 6,
  maxLength: 128,
  requireUppercase: false,
  requireLowercase: false,
  requireNumbers: false,
  requireSpecialChars: false,
};

/**
 * Validates an email address using regex pattern matching
 *
 * @param {string} email - The email address to validate
 * @returns {Object} Validation result object
 * @returns {boolean} returns.isValid - Whether the email is valid
 * @returns {string|null} returns.error - Error message if invalid
 * @returns {string|null} returns.code - Error code for programmatic handling
 *
 * @example
 * const result = validateEmail('user@example.com');
 * if (!result.isValid) {
 *   console.error(result.error); // "Please enter a valid email address"
 * }
 */
export const validateEmail = email => {
  if (!email) {
    return {
      isValid: false,
      error: 'Email is required',
      code: 'EMAIL_REQUIRED',
    };
  }

  if (!EMAIL_REGEX.test(email)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address',
      code: 'EMAIL_INVALID',
    };
  }

  return {
    isValid: true,
    error: null,
    code: null,
  };
};

/**
 * Validate password
 */
export const validatePassword = password => {
  if (!password) {
    return {
      isValid: false,
      error: 'Password is required',
      code: 'PASSWORD_REQUIRED',
    };
  }

  if (password.length < PASSWORD_RULES.minLength) {
    return {
      isValid: false,
      error: `Password must be at least ${PASSWORD_RULES.minLength} characters`,
      code: 'PASSWORD_TOO_SHORT',
    };
  }

  if (password.length > PASSWORD_RULES.maxLength) {
    return {
      isValid: false,
      error: `Password must not exceed ${PASSWORD_RULES.maxLength} characters`,
      code: 'PASSWORD_TOO_LONG',
    };
  }

  return {
    isValid: true,
    error: null,
    code: null,
  };
};

/**
 * Validate admin login form
 */
export const validateAdminForm = ({ email, password }) => {
  const emailValidation = validateEmail(email);
  const passwordValidation = validatePassword(password);

  const errors = {};
  let isValid = true;

  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
    isValid = false;
  }

  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
    isValid = false;
  }

  return {
    isValid,
    errors,
    fieldErrors: {
      email: !emailValidation.isValid,
      password: !passwordValidation.isValid,
    },
  };
};

/**
 * Validate client login form
 */
export const validateClientForm = ({ TIN, contractId, alias }) => {
  const errors = {};
  let isValid = true;

  if (!TIN || String(TIN).trim().length === 0) {
    errors.TIN = 'TIN is required';
    isValid = false;
  }

  const hasContract = contractId && String(contractId).trim().length > 0;
  const hasAlias = alias && String(alias).trim().length > 0;

  if (!hasContract && !hasAlias) {
    errors.contractId = 'Provide contract ID or alias';
    errors.alias = 'Provide contract ID or alias';
    isValid = false;
  }

  return {
    isValid,
    errors,
    fieldErrors: {
      TIN: !!errors.TIN,
      contractId: !hasContract && !hasAlias,
      alias: !hasContract && !hasAlias,
    },
  };
};

/**
 * Validate EDS form submission
 */
export const validateEDSForm = ({
  selectedIndex,
  privacyAgreed,
  certificates = [],
}) => {
  const errors = {};
  let isValid = true;

  if (selectedIndex === null || selectedIndex === undefined) {
    errors.certificate = 'Please select a certificate';
    isValid = false;
  }

  if (!privacyAgreed) {
    errors.privacy = 'You must agree to the terms and conditions';
    isValid = false;
  }

  if (certificates.length === 0) {
    errors.certificates = 'No certificates available';
    isValid = false;
  }

  // Validate selected certificate
  if (selectedIndex !== null && certificates[selectedIndex]) {
    const selectedCert = certificates[selectedIndex];
    const now = new Date();
    const validTo = new Date(selectedCert.validTo);

    if (validTo <= now) {
      errors.certificate = 'Selected certificate has expired';
      isValid = false;
    }
  }

  return {
    isValid,
    errors,
  };
};

/**
 * Get password strength
 */
export const getPasswordStrength = password => {
  if (!password) return { strength: 0, text: 'Enter a password' };

  let score = 0;
  const feedback = [];

  // Length
  if (password.length >= 8) score += 1;
  else feedback.push('At least 8 characters');

  // Uppercase
  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('Include uppercase letters');

  // Lowercase
  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('Include lowercase letters');

  // Numbers
  if (/\d/.test(password)) score += 1;
  else feedback.push('Include numbers');

  // Special characters
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  else feedback.push('Include special characters');

  const strengthLevels = {
    0: { strength: 0, text: 'Very Weak', color: 'error' },
    1: { strength: 20, text: 'Weak', color: 'error' },
    2: { strength: 40, text: 'Fair', color: 'warning' },
    3: { strength: 60, text: 'Good', color: 'info' },
    4: { strength: 80, text: 'Strong', color: 'success' },
    5: { strength: 100, text: 'Very Strong', color: 'success' },
  };

  return {
    ...strengthLevels[score],
    feedback,
    score,
  };
};

/**
 * Debounce function for form validation
 */
export const createDebouncedValidator = (validator, delay = 300) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    return new Promise(resolve => {
      timeoutId = setTimeout(() => {
        resolve(validator(...args));
      }, delay);
    });
  };
};

/**
 * Check if form has changes
 */
export const hasFormChanges = (currentValues, initialValues) => {
  return JSON.stringify(currentValues) !== JSON.stringify(initialValues);
};

/**
 * Rate limiting checker
 */
export const checkRateLimit = (lastAttemptTime, cooldownMs = 3000) => {
  if (!lastAttemptTime) return { allowed: true, remainingTime: 0 };

  const timeSinceLastAttempt = Date.now() - lastAttemptTime;
  const remainingTime = cooldownMs - timeSinceLastAttempt;

  return {
    allowed: remainingTime <= 0,
    remainingTime: Math.max(0, remainingTime),
  };
};
